import * as React from 'react';
import { PromptExit, navigate } from 'libs/router';
import { connect } from 'react-redux';

import ActionButton from 'components/buttons/action-button/ActionButton';
import SVGDynamicIcon from 'components/svg-dynamic-icon/SVGDynamicIcon';
import CloudinaryImage from 'components/CloudinaryImage';
import InputCheckbox from 'components/inputs/input-checkbox/InputCheckbox';
import ActionLink from 'components/buttons/action-link/ActionLink';
import TitleWithBackTo from 'components/title-with-back-to/TitleWithBackTo';

import BaseInfluencerAgencyPage from 'modules/influencers-agency/pages/BaseInfluencerAgencyPage';

import {
  InfluencerProfileFormWithFacebookState,
  createInfluencerProfiles,
  resetAllInfluencerProfile,
  resetInfluencerProfile } from 'modules/influencers-agency/reducers/influencers-agencies-reducer';
import { hideConfirmModal, showConfirmModal } from 'redux-store/reducers/modal-reducer';

import { __t, applyTranslations } from 'utils/language';

import './influencers-agency-create-multi-profile-page.scss';

applyTranslations(require('../../influencers-agencies-pages.i18n.json'));
applyTranslations(require('./influencers-agency-create-multi-profile-page.i18n.json'));

import { Params } from 'types';

type OwnProps = {
  params: Params
}

type StateProps = {
  profileMap: {
    [id: string]: InfluencerProfileFormWithFacebookState
  }
}

type DispatchProps = {
  dispatchResetInfluencerProfile: (profileId: string) => void,
  dispatchShowConfirmModal: (params) => void,
  dispatchHideConfirmModal: () => void,
  dispatchResetAllInfluencerProfile: () => void,
  dispatchCreateMultipleProfiles: (agencyId: number, profiles: Array<InfluencerProfileFormWithFacebookState>) => Promise<boolean>
}

type Props = OwnProps & StateProps & DispatchProps;

const MSG_KEY = 'influencers_agency_create_multi_profile_page';

const InfluencersAgencyCreateMultiProfilePage = ({
  params,
  profileMap,
  dispatchResetInfluencerProfile,
  dispatchShowConfirmModal,
  dispatchHideConfirmModal,
  dispatchResetAllInfluencerProfile,
  dispatchCreateMultipleProfiles
}: Props) => {
  const [hasWarrantySheetConsent, setHasWarrantySheetConsent] = React.useState(false);
  const [editProfileId, seteditProfileId] = React.useState<null | string>(null);

  const agencyId = Number(params.id);
  const numberOfProfiles = Object.keys(profileMap).length;

  React.useEffect(() => {
    if (editProfileId === 'new-profile') {
      navigate.to(`/influencers-agencies/${agencyId}/settings/create-new-profile`);
    } else if (editProfileId) {
      navigate.to(`/influencers-agencies/${agencyId}/settings/create-new-profile?profileId=${editProfileId}`);
    }
  }, [editProfileId]);

  const handleEdit = (profileId: string) => {
    seteditProfileId(profileId);
  };

  const handleDelete = (profileId: string) => {
    dispatchShowConfirmModal({
      message: __t(`${MSG_KEY}.delete_profile_modal.message`),
      title: __t(`${MSG_KEY}.delete_profile_modal.title`),
      confirmButtonLabel: __t(`${MSG_KEY}.delete_profile_modal.confirm_btn`),
      cancelButtonLabel: __t(`${MSG_KEY}.delete_profile_modal.cancel_btn`),
      onConfirm: () => {
        dispatchResetInfluencerProfile(profileId);
        dispatchHideConfirmModal();
      },
      onCancel: dispatchHideConfirmModal
    });
  };

  const handleSubmit = () => {
    dispatchCreateMultipleProfiles(agencyId, Object.keys(profileMap).map(profileId => profileMap[profileId]))
      .then(response => {
        if (response) {
          dispatchResetAllInfluencerProfile();
          navigate.to(`/influencers-agencies/${agencyId}/settings/profiles-overview`);
        }
      });
  };

  const handleCancel = () => {
    dispatchShowConfirmModal({
      message: __t(`${MSG_KEY}.cancel_submit_profile_modal.message`),
      title: __t(`${MSG_KEY}.cancel_submit_profile_modal.title`),
      confirmButtonLabel: __t(`${MSG_KEY}.cancel_submit_profile_modal.confirm_btn`),
      cancelButtonLabel: __t(`${MSG_KEY}.cancel_submit_profile_modal.cancel_btn`),
      onConfirm: () => {
        dispatchResetAllInfluencerProfile();
        dispatchHideConfirmModal();
      },
      onCancel: dispatchHideConfirmModal
    });
  };

  const handleCheckedChange = (): void => {
    setHasWarrantySheetConsent(!hasWarrantySheetConsent);
  };

  const handleWarrantySheetClick = (): void => {
    dispatchShowConfirmModal({
      width: 'medium',
      innerContent: (
        <div className="influencers-agency-create-multi-profile-page__modal-info">
          {__t(`${MSG_KEY}.warranty_sheet.message`)}
        </div>
      ),
      title: __t(`${MSG_KEY}.warranty_sheet.title`),
      confirmButtonLabel: __t(`${MSG_KEY}.warranty_sheet.close_btn`),
      onConfirm: dispatchHideConfirmModal,
      onClose: dispatchHideConfirmModal
    });
  };

  return (
    <BaseInfluencerAgencyPage
      agencyId={agencyId}
      documentTitle={__t('influencers_agencies_pages.add_multiple_profiles')}
    >
      <div style={{ maxWidth: '642px' }}>
        {
          numberOfProfiles > 0 && !editProfileId &&
            <PromptExit
              type="page"
              message={__t(`${MSG_KEY}.cancel_submit_profile_modal.message`)}
              title={__t(`${MSG_KEY}.cancel_submit_profile_modal.title`)}
              actionButtonLabel={__t(`${MSG_KEY}.cancel_submit_profile_modal.confirm_btn`)}
              cancelButtonLabel={__t(`${MSG_KEY}.cancel_submit_profile_modal.cancel_btn`)}
            />
        }
        <TitleWithBackTo
          title={__t(`${MSG_KEY}.add_new_profile`)}
          backToUrl={`/influencers-agencies/${agencyId}/settings/profiles-overview`}
        />
        <div className="influencers-agency-create-multi-profile-page--info-text">
          {__t(`${MSG_KEY}.info_text`)}
        </div>
        <div className="influencers-agency-create-multi-profile-page--btn-container">
          <ActionButton
            width="minimum"
            onClick={() => {
              seteditProfileId('new-profile');
            }}
          >
            <SVGDynamicIcon
              name="plus-custom-fg"
              width="8px"
              height="8px"
              customColor="#3a3a3a"
            />
            <span>{__t(`${MSG_KEY}.create_new_profile_btn`)}</span>
          </ActionButton>
        </div>
        <div className="influencers-agency-create-multi-profile-page--profile-list">
          {
            Object.keys(profileMap).map(profileId => (
              <div
                className="influencers-agency-create-multi-profile-page--profile-list-item"
                key={profileId}
              >
                <div className="influencers-agency-create-multi-profile-page--profile-list-item-image-name">
                  <CloudinaryImage
                    width={24}
                    height={24}
                    className="influencers-agency-create-multi-profile-page--profile-list-item-image"
                    image={profileMap[profileId].profileImage}
                  />
                  <div className="influencers-agency-create-multi-profile-page--profile-list-item-name">
                    {`${profileMap[profileId].influencerFirstName} ${profileMap[profileId].influencerLastName}`}
                  </div>
                </div>
                <div className="influencers-agency-create-multi-profile-page--profile-list-item-action">
                  <SVGDynamicIcon
                    name="pencil-custom-bg"
                    customColor="black"
                    width={16}
                    height={16}
                    clickable
                    onClick={() => handleEdit(profileId)}
                  />
                  <SVGDynamicIcon
                    name="trash-custom-fg"
                    customColor="black"
                    width={16}
                    height={16}
                    clickable
                    onClick={() => handleDelete(profileId)}
                  />
                </div>
              </div>
            ))
          }
        </div>
        {
          numberOfProfiles > 0 && (
            <div className="influencers-agency-create-multi-profile-page__warranty-sheet-checkbox">
              <InputCheckbox
                id="influencers-agency-create-multi-profile-page-warranty-sheet"
                value={String(hasWarrantySheetConsent)}
                checked={hasWarrantySheetConsent}
                onChange={handleCheckedChange}
              />
              <div className="influencers-agency-create-multi-profile-page__warranty-sheet-text">
                <span>{__t(`${MSG_KEY}.warranty_sheet_checkbox_text_1`)}</span>
                <ActionLink
                  title={`${MSG_KEY}.warranty_sheet_link`}
                  size="normal"
                  color="primary"
                  onClick={handleWarrantySheetClick}
                >
                  {__t(`${MSG_KEY}.warranty_sheet_link`)}
                </ActionLink>
                <span>{__t(`${MSG_KEY}.warranty_sheet_checkbox_text_2`)}</span>
              </div>
            </div>
          )
        }
        {
          numberOfProfiles > 0 && (
            <div className="influencers-agency-create-multi-profile-page__action-container">
              <ActionButton
                color="tertiary"
                onClick={handleCancel}
              >
                {__t(`${MSG_KEY}.cancel_btn`)}
              </ActionButton>
              <ActionButton
                color="primary"
                onClick={handleSubmit}
                disabled={!hasWarrantySheetConsent}
              >
                {__t(`${MSG_KEY}.submit_btn`, { numberOfProfiles, withPlural: numberOfProfiles })}
              </ActionButton>
            </div>
          )
        }
      </div>
    </BaseInfluencerAgencyPage>
  );
};

const mapStateToProps = ({ influencersAgency }): StateProps => ({
  profileMap: influencersAgency.newInfluencerProfiles
});

const mapDispatchToProps = (dispatch): DispatchProps => ({
  dispatchResetInfluencerProfile: (profileId) => dispatch(resetInfluencerProfile(profileId)),
  dispatchResetAllInfluencerProfile: () => dispatch(resetAllInfluencerProfile()),
  dispatchShowConfirmModal: (params) => dispatch(showConfirmModal(params)),
  dispatchHideConfirmModal: () => dispatch(hideConfirmModal()),
  dispatchCreateMultipleProfiles: (agencyId, profiles) => dispatch(createInfluencerProfiles(agencyId, profiles))
});

export default connect(mapStateToProps, mapDispatchToProps)(InfluencersAgencyCreateMultiProfilePage);
